import React, { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { SUPERADMIN_ROUTES } from './routes';
import { SuperadminProtectedRoute } from './SuperadminProtectedRoute';
import { SuperadminHomeRedirect } from './SuperadminHomeRedirect';
import { LoadingSpinner } from '@/components/ui';

// Lazy load components for code splitting
const SuperadminLoginPage = lazy(() => import('../pages/auth/LoginPage'));
const SuperadminDashboardPage = lazy(() => import('../pages/DashboardPage'));
const UsersPage = lazy(() => import('../pages/users/pages/UsersPage/UsersPage'));
const RolesPage = lazy(() => import('../pages/roles/pages/RolesPage/RolesPage'));
const PermissionsPage = lazy(() => import('../pages/permissions/pages/PermissionsPage/PermissionsPage'));
const RolePermissionsPage = lazy(() => import('../pages/rolePermissions/pages/RolePermissionsPage/RolePermissionsPage'));
const UserPermissionsPage = lazy(() => import('../pages/userPermissions/pages/UserPermissionsPage/UserPermissionsPage'));
const SuperadminTransactionsPage = lazy(() => import('../pages/transactions/pages/TransactionsPage/TransactionsPage'));
const SuperadminWebhooksPage = lazy(() => import('../pages/webhooks/pages/WebhooksPage/WebhooksPage'));
const SuperadminSettlementPage = lazy(() => import('../pages/settlement/SettlementPage'));
const SuperadminProfilePage = lazy(() => import('../pages/profile/ProfilePage'));

// Layouts
const SuperadminMainLayout = lazy(() => import('../layouts/SuperadminMainLayout'));
const AuthLayout = lazy(() => import('@/layouts/AuthLayout'));

// Wrapper component for lazy-loaded routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense
    fallback={
      <LoadingSpinner
        title="Loading Unity Superadmin..."
        subtitle="Please wait while we prepare your experience"
        size="medium"
        color="white"
      />
    }
  >
    {children}
  </Suspense>
);

// Create superadmin routes configuration
export const superadminRoutes: RouteObject[] = [
  {
    path: SUPERADMIN_ROUTES.HOME,
    element: <SuperadminHomeRedirect />,
  },
  {
    path: '/superadmin/auth',
    element: (
      <LazyRoute>
        <AuthLayout />
      </LazyRoute>
    ),
    children: [
      {
        path: 'login',
        element: (
          <LazyRoute>
            <SuperadminLoginPage />
          </LazyRoute>
        ),
      },
    ],
  },
  {
    path: '/superadmin',
    element: (
      <SuperadminProtectedRoute>
        <LazyRoute>
          <SuperadminMainLayout />
        </LazyRoute>
      </SuperadminProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: (
          <LazyRoute>
            <SuperadminDashboardPage />
          </LazyRoute>
        ),
      },
      {
        path: 'users',
        element: (
          <LazyRoute>
            <UsersPage />
          </LazyRoute>
        ),
      },
      {
        path: 'roles',
        element: (
          <LazyRoute>
            <RolesPage />
          </LazyRoute>
        ),
      },
      {
        path: 'permissions',
        element: (
          <LazyRoute>
            <PermissionsPage />
          </LazyRoute>
        ),
      },
      {
        path: 'role-permissions',
        element: (
          <LazyRoute>
            <RolePermissionsPage />
          </LazyRoute>
        ),
      },
      {
        path: 'user-permissions',
        element: (
          <LazyRoute>
            <UserPermissionsPage />
          </LazyRoute>
        ),
      },
      {
        path: 'transactions',
        element: (
          <LazyRoute>
            <SuperadminTransactionsPage />
          </LazyRoute>
        ),
      },
      {
        path: 'webhooks',
        element: (
          <LazyRoute>
            <SuperadminWebhooksPage />
          </LazyRoute>
        ),
      },
      {
        path: 'settlement',
        element: (
          <LazyRoute>
            <SuperadminSettlementPage />
          </LazyRoute>
        ),
      },
      {
        path: 'profile',
        element: (
          <LazyRoute>
            <SuperadminProfilePage />
          </LazyRoute>
        ),
      },
    ],
  },
  {
    path: SUPERADMIN_ROUTES.LOGIN,
    element: <Navigate to="/superadmin/auth/login" replace />,
  },
  {
    path: SUPERADMIN_ROUTES.NOT_FOUND,
    element: (
      <LazyRoute>
        <div>Superadmin - 404 Not Found</div>
      </LazyRoute>
    ),
  },
  {
    path: SUPERADMIN_ROUTES.UNAUTHORIZED,
    element: (
      <LazyRoute>
        <div>Superadmin - Unauthorized Access</div>
      </LazyRoute>
    ),
  },
];

